import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModakBackdrop from "./ModakBackdrop";
import { useRoundGate } from "./useRoundGate";
import { useElapsedTimer } from "./useElapsedTimer";
import { submitModakRound } from "./modakApi";
import { RoundHeader, InfoModal } from "./RoundChrome";
import { buildWordSearchGrid, straightLinePath } from "./wordSearchGrid";

const GRID_SIZE = 14;
const WORDS = [
  { key: "AUSPICIOUSNESS", label: "Auspiciousness" },
  { key: "GOODLUCK", label: "Good luck" },
  { key: "WISDOM", label: "Wisdom" },
  { key: "UNDERSTANDING", label: "Understanding" },
  { key: "WEALTH", label: "Wealth" },
  { key: "HEALTH", label: "Health" },
  { key: "STRENGTH", label: "Strength" },
  { key: "ENDURANCE", label: "Endurance" },
];

const RULES = [
  "Identify the words in the Puzzle",
  "Drag and Select the identified words and they will be highlighted",
  "Clicking on a hint each time will add 15 seconds",
];

const cellKey = (r, c) => `${r},${c}`;

export default function Round4() {
  const navigate = useNavigate();
  const { loading, user, adminToken, timerKey, status } = useRoundGate(4);
  const [hintSeconds, setHintSeconds] = useState(0);
  const time = useElapsedTimer(timerKey, hintSeconds);

  const grid = useMemo(() => buildWordSearchGrid(WORDS.map((w) => w.key), GRID_SIZE), []);

  const [score, setScore] = useState(0);
  const [foundWords, setFoundWords] = useState({});
  const [foundCells, setFoundCells] = useState({});
  const [path, setPath] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scoreRef = useRef(0);
  const selectingRef = useRef(null);
  const gridWrapRef = useRef(null);
  const basePointsRef = useRef(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (status) {
      scoreRef.current = status.points ?? 0;
      basePointsRef.current = scoreRef.current;
      setScore(scoreRef.current);
    }
  }, [status]);

  const finish = useCallback(
    async (finalPoints) => {
      // Ref guard so a fast double-click on Submit/SKIP can't race two
      // finish() calls through before `submitting` state re-renders — see
      // Round1.jsx for the full explanation.
      if (submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);
      const answers = WORDS.map((w) => (foundWords[w.key] ? w.label : "")).join(",");
      await submitModakRound(adminToken, {
        userId: user.userId,
        round: 4,
        points: finalPoints,
        time,
        answers,
        user,
      });
      navigate("/home");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminToken, user, time, foundWords, navigate]
  );

  const cellFromPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    const td = el && el.closest ? el.closest("td[data-row]") : null;
    if (!td) return null;
    return [Number(td.dataset.row), Number(td.dataset.col)];
  };

  const finalizeSelection = useCallback(() => {
    const current = path;
    selectingRef.current = null;
    setPath([]);
    if (current.length < 2) return;

    const forward = current.map(([r, c]) => grid[r][c]).join("");
    const backward = forward.split("").reverse().join("");

    const match = WORDS.find((w) => !foundWords[w.key] && (w.key === forward || w.key === backward));
    if (!match) return;

    const cellsUpdate = {};
    current.forEach(([r, c]) => {
      cellsUpdate[cellKey(r, c)] = true;
    });
    setFoundCells((prev) => ({ ...prev, ...cellsUpdate }));
    setFoundWords((prev) => ({ ...prev, [match.key]: true }));

    scoreRef.current += 1;
    setScore(scoreRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, grid, foundWords]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!selectingRef.current) return;
      const point = "touches" in e ? e.touches[0] : e;
      const cell = cellFromPoint(point.clientX, point.clientY);
      if (!cell) return;
      const [r0, c0] = selectingRef.current;
      const line = straightLinePath(r0, c0, cell[0], cell[1]);
      if (line) setPath(line);
    };
    const handleUp = () => {
      if (selectingRef.current) finalizeSelection();
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [finalizeSelection]);

  if (loading) return null;

  const startSelection = (r, c) => {
    selectingRef.current = [r, c];
    setPath([[r, c]]);
  };

  const foundCount = Object.keys(foundWords).length;
  const canSubmit = foundCount >= 4;

  const handleSubmit = () => {
    if (!canSubmit || submitting) return;
    finish(scoreRef.current);
  };

  const handleSkip = () => {
    if (submitting) return;
    finish(basePointsRef.current);
  };

  const handleHint = () => {
    setHintSeconds((s) => s + 15);
    setShowHint(true);
  };

  const isInPath = (r, c) => path.some(([pr, pc]) => pr === r && pc === c);

  return (
    <ModakBackdrop>
      <div className="modak-round">
        <RoundHeader
          time={time}
          score={score}
          rules={RULES}
          onSkip={handleSkip}
          extra={
            <button
              type="button"
              className="modak-pill modak-pill--icon"
              onClick={handleHint}
              aria-label="Hint"
              title="Hint"
            >
              💡
            </button>
          }
        />

        <div className="modak-round-heading-row">
          <span className="modak-round-label">Symbols</span>
          <div className="modak-round-instruction">Find the key words that symbolize the festival</div>
        </div>

        <div className="modak-r4-grid-wrap" ref={gridWrapRef}>
          <table className="modak-r4-grid">
            <tbody>
              {grid.map((row, r) => (
                <tr key={r}>
                  {row.map((letter, c) => {
                    const key = cellKey(r, c);
                    const cls = foundCells[key] ? "is-found" : isInPath(r, c) ? "is-selecting" : "";
                    return (
                      <td
                        key={c}
                        data-row={r}
                        data-col={c}
                        className={cls}
                        onPointerDown={() => startSelection(r, c)}
                      >
                        {letter}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modak-r4-answers">
          {WORDS.map((w) => (
            <input key={w.key} type="text" readOnly disabled value={foundWords[w.key] ? w.label : ""} />
          ))}
        </div>

        <button type="button" className="modak-btn modak-submit-btn" disabled={!canSubmit || submitting} onClick={handleSubmit}>
          Submit
        </button>
      </div>

      {showHint && (
        <InfoModal title="Hint" onClose={() => setShowHint(false)}>
          <div className="modak-rules-list">
            {WORDS.map((w, i) => (
              <div key={w.key}>
                {i + 1}. {w.label}
              </div>
            ))}
          </div>
        </InfoModal>
      )}
    </ModakBackdrop>
  );
}
